import { motion, useReducedMotion } from 'framer-motion';
import { useMemo, useRef, useState } from 'react';
import { useContainerWidth } from '../../hooks/use-responsive-size';
import { SWIFTFOX_PALETTE } from '../../palette';
import { Box } from '../box/index';

const AnimatedBox = motion(Box);
const colors = [
	SWIFTFOX_PALETTE.blue['40-coreBlue'],
	SWIFTFOX_PALETTE.blue['30-skywave'],
	SWIFTFOX_PALETTE.blue['20-frostedAzure'],
	SWIFTFOX_PALETTE.orange['30-highlight'],
	SWIFTFOX_PALETTE.orange['20-goldenSand']
];
const segmentHeight = 14;
const overshoot = 124;
const stagger = 0.1;
const transitionDuration = 0.7;
const holdDuration = 1.2;

// Motion timings and batch layout follow Kiernan's reference: https://jsfiddle.net/kiernan_trfg/f61ytxjL/
export function DataStreamProgress({ label }: { label: string }) {
	const container = useRef<HTMLDivElement>(null);
	const width = useContainerWidth(container);
	const reducedMotion = useReducedMotion();
	const [cycle, setCycle] = useState(0);
	const segments = useMemo(() => {
		const result: { x: number; width: number; color: string }[] = [];
		let x = -overshoot;
		let previousColor = -1;
		while (x < width + overshoot) {
			const segmentWidth = (1 + Math.floor(Math.random() * 6)) * 13.5;
			const colorIndex = (previousColor + 1 + Math.floor(Math.random() * (colors.length - 1))) % colors.length;
			result.push({ x, width: segmentWidth, color: colors[colorIndex] });
			x += segmentWidth + 8;
			previousColor = colorIndex;
		}
		return result;
	}, [width, cycle]);
	const inTime = transitionDuration + (segments.length - 1) * stagger;
	const outStart = inTime + holdDuration;
	const duration = outStart + inTime;

	return (
		<Box ref={container} role="progressbar" aria-label={label} sx={{ overflow: 'hidden', height: 32, position: 'relative' }}>
			<AnimatedBox
				key={`${width}-${cycle}-${reducedMotion}`}
				aria-hidden="true"
				initial={{ x: 0 }}
				animate={{ x: reducedMotion ? 0 : overshoot }}
				transition={{ duration, ease: 'linear' }}
				onAnimationComplete={reducedMotion ? undefined : () => setCycle(value => value + 1)}
			>
				{segments.map((segment, index) => {
					const delay = (segments.length - index - 1) * stagger;
					const start = -segment.width - overshoot;
					const end = width + segment.width;
					return (
						<AnimatedBox
							key={index}
							initial={{ x: reducedMotion ? segment.x : start }}
							animate={{ x: reducedMotion ? segment.x : [start, start, segment.x, segment.x, end, end] }}
							transition={{
								duration,
								times: [
									0,
									delay / duration,
									(delay + transitionDuration) / duration,
									(outStart + delay) / duration,
									(outStart + delay + transitionDuration) / duration,
									1
								],
								ease: ['linear', 'easeOut', 'linear', 'easeIn', 'linear']
							}}
							sx={{
								position: 'absolute',
								top: 9,
								width: segment.width,
								height: segmentHeight,
								bgcolor: segment.color,
								borderRadius: `${segmentHeight / 10}px`
							}}
						/>
					);
				})}
			</AnimatedBox>
		</Box>
	);
}
